// Single Zustand store sliced by domain. See REACT_COMPONENT_PLAN.md §4.
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  Density,
  FilterGroup,
  PageSize,
  RulePath,
} from "../types/inpact";
import { EMPTY_FILTER_GROUP } from "../lib/api";
import { migrateRetiredFields, removeAtPath } from "../lib/filterLogic";

export type Theme = "light" | "dark" | "system";

interface PersistedPrefs {
  density: Density;
  filterRailWidth: number;
  theme: Theme;
}

interface ExplorerState extends PersistedPrefs {
  filterGroup: FilterGroup;
  page: number;
  pageSize: PageSize;
  selectedContactId: string | null;
  advancedFilterOpen: boolean;
  // Seed for the builder's draft when it's opened for a specific filter that
  // isn't (yet) the live one — e.g. AnaMoni's "Edit in builder" on an older
  // chat answer. Deliberately NOT persisted (see partialize): like
  // advancedFilterOpen, a stale seed surviving a reload is a bug, not a
  // restored preference.
  advancedFilterSeed: FilterGroup | null;
  policyDefaultInitialized: boolean;

  setFilterGroup: (g: FilterGroup) => void;
  initializePolicyDefault: (g: FilterGroup) => void;
  removeRuleAt: (path: RulePath) => void;
  resetFilters: () => void;
  openAdvancedFilter: (seed?: FilterGroup) => void;
  closeAdvancedFilter: () => void;

  setPage: (p: number) => void;
  setPageSize: (s: PageSize) => void;

  selectContact: (id: string | null) => void;

  setDensity: (d: Density) => void;
  setFilterRailWidth: (w: number) => void;
  setTheme: (t: Theme) => void;
}

export const useExplorerStore = create<ExplorerState>()(
  persist(
    (set) => ({
      filterGroup: EMPTY_FILTER_GROUP,
      page: 1,
      pageSize: 50,
      selectedContactId: null,
      advancedFilterOpen: false,
      advancedFilterSeed: null,
      policyDefaultInitialized: false,
      density: "comfortable",
      filterRailWidth: 280,
      theme: "system",

      // Every filter entering the store is normalised, whatever put it
      // there — the rail, the builder, a saved view, an AnaMoni answer, or
      // one restored from localStorage across the migration. The rail
      // matches its facets by exact field key, so one retired key in the
      // live filter is a rule the user can see in the chips but not in the
      // facet that owns it (see `migrateRetiredFields`).
      setFilterGroup: (g) => set({
        filterGroup: migrateRetiredFields(g),
        policyDefaultInitialized: true,
        page: 1,
      }),
      initializePolicyDefault: (g) => set((state) => (
        state.policyDefaultInitialized
          ? state
          : {
              filterGroup: migrateRetiredFields(g),
              policyDefaultInitialized: true,
              page: 1,
            }
      )),
      removeRuleAt: (path) =>
        set((s) => ({
          filterGroup: removeAtPath(s.filterGroup, path),
          policyDefaultInitialized: true,
          page: 1,
        })),
      resetFilters: () => set({
        filterGroup: EMPTY_FILTER_GROUP,
        policyDefaultInitialized: true,
        page: 1,
      }),
      // Deliberately NOT persisted (see partialize): a modal that reopens
      // itself after a page reload is a bug, not a restored preference. The
      // optional seed lets a caller (e.g. AnaMoni's "Edit in builder") hand
      // the modal a specific filter to edit WITHOUT committing it to the live
      // `filterGroup` first — Apply is what commits it; Cancel leaves the
      // live filter untouched.
      openAdvancedFilter: (seed) =>
        set({
          advancedFilterOpen: true,
          advancedFilterSeed: seed ? migrateRetiredFields(seed) : null,
        }),
      closeAdvancedFilter: () => set({ advancedFilterOpen: false, advancedFilterSeed: null }),

      setPage: (page) => set({ page }),
      setPageSize: (pageSize) => set({ pageSize, page: 1 }),

      selectContact: (id) => set({ selectedContactId: id }),

      setDensity: (density) => set({ density }),
      setFilterRailWidth: (filterRailWidth) => set({ filterRailWidth }),
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: "inpact:explorer-prefs",
      partialize: (s): PersistedPrefs => ({
        density: s.density,
        filterRailWidth: s.filterRailWidth,
        theme: s.theme,
      }),
    },
  ),
);
